import React, { useContext, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp, mdiCog } from '@mdi/js';
import { localStorageRepositorySingleton } from '@warehouse/shared/util';

// GRAPHQL
import { TitleCommon, TitleFull as Title } from '@warehouse/title/core';
import { GoToParentButton, shouldDisplayGoToParentButton } from '@warehouse/title/feature-go-to-parent';
import { getBackgroundColor, getWorkTypeObject, movieUuid } from '@warehouse/title/infra';
import { titleEditorStoreSelector, useTitleEditorStore } from '@warehouse/title/domain';

// COMPONENTS
import { Poster, Manifestation } from '@warehouse/title/feature-title-overview';
import { EditViewModeToggleButton } from '@warehouse/shared/ui';
import Button from '../../library/Button';

// JSONS
import availableWorkTypeDetailsByWorkType from '../../../assets/json-administration-profiles/availableWorkTypeDetailsByWorkType.json';

// HOOKS
import {
	ActionsWrapper,
	BackgroundColorOpacity,
	CollapseAction,
	CollapsedWrapper,
	PosterWrapper,
	RightWrapper,
	TopBarWrapper,
	TopWrapper,
	Wrapper,
} from './style';
import { Compilation, Episode, Movie, Performance, Season, Series, Supplemental } from './layouts';
import getDeepProperty from '../../../utils/getDeepProperty';
import { fromBasic } from '../../../utils/titleGetProperty';
import { TitleOverviewByTypeProps } from './layouts/type';
import { UnsavedChangesContext } from '../../../views/title/contexts/UnsavedChangesContext';
import { TitleOverviewTopBar } from '../../../../libs/title/ui/TitleOverviewTopBar';

type TitleOverviewProps = {
	titleFull: Title;
} & CommonTitleOverviewProps;

type TitleOverviewLoaderProps = {
	titleFull?: Title;
	titleSearchDataIndexed?: TitleCommon;
} & CommonTitleOverviewProps;

type CommonTitleOverviewProps = {
	onClickSettings: () => void;
};

type KeyofJson = keyof typeof availableWorkTypeDetailsByWorkType;

export const TITLE_OVERVIEW_COLLAPSED_KEY = 'title_overview_collapsed_key';

function getWorkTypeNameByUuid(workTypeUuid: string) {
	const keys = Object.keys(availableWorkTypeDetailsByWorkType || {});

	let found;

	keys.forEach((key) => {
		if (availableWorkTypeDetailsByWorkType[key as KeyofJson].uuid === workTypeUuid) found = key;
	});

	return found;
}

function getWorkTypeByName(name: KeyofJson) {
	return availableWorkTypeDetailsByWorkType[name];
}

function TitleOverview({ titleFull, onClickSettings }: TitleOverviewProps) {
	const { promptUserIfUnsavedChanges } = useContext(UnsavedChangesContext);

	// variables
	const workType = getDeepProperty<string | undefined>(titleFull, fromBasic(['workType']));
	const workTypeName = getWorkTypeNameByUuid(workType || '');
	const backgroundColor = getBackgroundColor(workTypeName);
	const parentAbstractionWorkTypeName = titleFull.readonly.parentAbstractionWorkType
		? getWorkTypeObject(titleFull.readonly.parentAbstractionWorkType).label
		: undefined;

	const { setEditMode } = useTitleEditorStore(titleEditorStoreSelector.actions);
	const editMode = useTitleEditorStore(titleEditorStoreSelector.editMode);

	// state
	const [collapsed, setCollapsed] = useState<boolean>(() => {
		const collapsedState = localStorageRepositorySingleton.get().get(TITLE_OVERVIEW_COLLAPSED_KEY);

		return collapsedState === "true"; // Default to open title overview
	});

	useEffect(() => {
		localStorageRepositorySingleton.get().set(TITLE_OVERVIEW_COLLAPSED_KEY, collapsed.toString());
	}, [collapsed]);

	const handleToggleCollapse = () => setCollapsed((col) => !col);

	const topBar = (
		<TopBarWrapper>
			<TitleOverviewTopBar
				titleCommon={titleFull}
				actions={
					<ActionsWrapper>
						{titleFull && shouldDisplayGoToParentButton(titleFull) && (
							<GoToParentButton style={{ marginRight: 16 }} title={titleFull} />
						)}
						<Typography variant="buttonMediumMedium">Mode</Typography>
						<EditViewModeToggleButton onClickHandler={
							(editModeValue: boolean) =>
									promptUserIfUnsavedChanges(() => {
										setEditMode(editModeValue);
									})
								} editMode={editMode} />

						<Button onClick={onClickSettings} aria-label="Open Title Settings" nxstyle="secondary-black">
							<Icon path={mdiCog} size="16px" />
						</Button>
					</ActionsWrapper>
				}
			/>
		</TopBarWrapper>
	);

	if (collapsed) {
		return (
			<>
				<BackgroundColorOpacity color={backgroundColor} />
				<CollapsedWrapper>
					{topBar}
					<CollapseAction 
						onClick={() => handleToggleCollapse()}
					>
						<Icon aria-label="expand-button" path={mdiChevronDown} size="16px" />
					</CollapseAction>
				</CollapsedWrapper>
			</>
		);
	}

	const titleOverviewByTitleTypeProps: TitleOverviewByTypeProps = {
		displayVideoPlayer: false, // Should be base on titleIsPerformance
		topBar,
		editMode,
		title: titleFull,
		parentAbstractionWorkType:
			workType === getWorkTypeByName('Edit')?.uuid &&
			parentAbstractionWorkTypeName &&
			['Movie', 'Episode', 'Supplemental'].includes(parentAbstractionWorkTypeName)
				? (parentAbstractionWorkTypeName as 'Movie' | 'Episode' | 'Supplemental')
				: undefined,
	};

	const titleOverviewByTitleType = {
		[getWorkTypeByName('Compilation')?.uuid || 'Compilation']: <Compilation {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Episode')?.uuid || 'Episode']: <Episode {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Movie')?.uuid || 'Movie']: <Movie {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Edit')?.uuid || 'Edit']: <Performance {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Series')?.uuid || 'Series']: <Series {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Season')?.uuid || 'Season']: <Season {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Supplemental')?.uuid || 'Supplemental']: <Supplemental {...titleOverviewByTitleTypeProps} />,
		[getWorkTypeByName('Manifestation')?.uuid || 'Manifestation']: <Manifestation {...titleOverviewByTitleTypeProps} />,
	};

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const titleOverview = titleOverviewByTitleType[workType || movieUuid()];

	return (
		<TopWrapper>
			<BackgroundColorOpacity color={backgroundColor} />
			<Wrapper>
				{titleOverview}
				<RightWrapper displayVideoPlayer={false}>
					<PosterWrapper displayVideoPlayer={false}>
						{' '}
						{/* Should be base on titleIsPerformance */}
						<Poster />
					</PosterWrapper>
				</RightWrapper>
			</Wrapper>
			<CollapseAction 
				onClick={() => handleToggleCollapse()}
			>
				<Icon aria-label="collapse-button" path={mdiChevronUp} size="16px" />
			</CollapseAction>
		</TopWrapper>
	);
}

function TitleOverviewLoader(props: TitleOverviewLoaderProps) {
	const { titleFull } = props;
	if (!titleFull) return null;

	return <TitleOverview {...props} titleFull={titleFull} />;
}

export default TitleOverviewLoader;
